import { Button, Tag, Typography } from 'antd';

import {
  useClientSubscriptions,
  useClientSubscriptionsEffects,
} from '@app/components/RecordModal/components/RecordDetails/components/ClientSubscriptions/hooks';
import type { ClientSubscriptionView } from '@app/components/RecordModal/components/RecordDetails/components/ClientSubscriptions/utils';

import type { FC } from 'react';

import './ClientSubscriptions.scss';

const { Text } = Typography;

interface ClientSubscriptionsProps {
  clientId: number | null;
  activeBranchId: number | null;
  /** Абонементы, услуги которых уже добавлены в запись */
  appliedClientSubscriptionIds: number[];
  onToggle(subscription: ClientSubscriptionView): void;
  disabled?: boolean;
}

/**
 * Абонементы клиента над таблицей «Услуги» окна записи:
 * «Есть абонемент: <название> — N пос.» и остаток по услугам (ТЗ us/11427).
 * Срок действия GetClientSubscriptions не отдаёт, поэтому его нет. Клик по
 * названию добавляет услуги абонемента в запись, повторный — убирает.
 */
export const ClientSubscriptions: FC<ClientSubscriptionsProps> = ({
  clientId,
  activeBranchId,
  appliedClientSubscriptionIds,
  onToggle,
  disabled,
}) => {
  const { subscriptions, error, showErrorHandler } = useClientSubscriptions({
    clientId,
    activeBranchId,
  });

  useClientSubscriptionsEffects({ error, showErrorHandler });

  if (subscriptions.length === 0) return null;

  return (
    <div className="ClientSubscriptions" data-testid="client-subscriptions">
      {subscriptions.map((subscription) => {
        const { clientSubscriptionId, name, available, servicesLabel } =
          subscription;
        const isApplied =
          appliedClientSubscriptionIds.includes(clientSubscriptionId);

        return (
          <div
            className="ClientSubscriptions__item"
            key={clientSubscriptionId}
            data-testid="client-subscription"
          >
            <Text>
              Есть абонемент:{' '}
              <Button
                type="link"
                className="ClientSubscriptions__name"
                disabled={disabled}
                title={
                  isApplied
                    ? 'Нажмите, чтобы убрать услуги абонемента из записи'
                    : 'Нажмите, чтобы добавить услуги абонемента в запись'
                }
                aria-pressed={isApplied}
                onClick={() => onToggle(subscription)}
                data-testid="client-subscription-toggle"
              >
                {name || 'Абонемент без названия'}
              </Button>{' '}
              — {available} пос.
            </Text>
            <Text type="secondary">Осталось: {servicesLabel}</Text>
            {isApplied && (
              <>
                <Tag color="green">Добавлен в запись</Tag>
                <Button
                  size="small"
                  disabled={disabled}
                  onClick={() => onToggle(subscription)}
                  data-testid="client-subscription-remove"
                >
                  Убрать из записи
                </Button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};
